import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Gender } from '../api/types';
import { GenderPicker } from '../components/GenderPicker';
import { MainPracticePicker } from '../components/MainPracticePicker';
import { TraditionPicker } from '../components/TraditionPicker';
import { useUser } from '../state/UserContext';
import type { Tradition } from '../theme/tokens';

export function Onboarding() {
  const { me, updateMe } = useUser();
  const navigate = useNavigate();
  const [selected, setSelected] = useState<Tradition[]>(me?.traditions ?? []);
  const [primaryTradition, setPrimaryTradition] = useState<Tradition | null>(me?.primaryTradition ?? null);
  const [gender, setGender] = useState<Gender | null>(me?.gender ?? null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleContinue = async () => {
    setSaving(true);
    setError(null);
    try {
      const effectivePrimary =
        selected.length === 1 ? selected[0] : selected.includes(primaryTradition as Tradition) ? primaryTradition : null;
      await updateMe({ traditions: selected, primaryTradition: effectivePrimary, gender });
      navigate('/home', { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong saving your traditions.');
    } finally {
      setSaving(false);
    }
  };

  const canContinue = gender !== null;
  const previewTradition = primaryTradition ?? (selected.length === 1 ? selected[0] : null);

  return (
    <div>
      <h1 className="page-title">Welcome to Omnist</h1>
      <p className="page-subtitle">
        Pick the wisdom traditions that speak to you. You can change this anytime in Settings ---
        it shapes your daily quotes, practices, and library.
      </p>

      <TraditionPicker selected={selected} onChange={setSelected} />

      {selected.length > 1 && (
        <>
          <div className="section-title">Choose your main practice</div>
          <p className="page-subtitle" style={{ marginBottom: 12 }}>
            The one your guide will be dressed for, and what we&rsquo;ll feature first on Home.
            The rest still shape your quotes and practices as secondary traditions.
          </p>
          <MainPracticePicker options={selected} selected={primaryTradition} onChange={setPrimaryTradition} />
        </>
      )}

      <div className="section-title">Choose your guide</div>
      <p className="page-subtitle" style={{ marginBottom: 12 }}>
        Who greets you on Home and walks you through your practices
        {previewTradition ? ' -- dressed for your main practice' : ''}.
      </p>
      <GenderPicker selected={gender} onChange={setGender} tradition={previewTradition} />

      {error && <p className="error-text">{error}</p>}

      <button
        type="button"
        className="btn btn-primary btn-block"
        onClick={handleContinue}
        disabled={saving || !canContinue}
        style={{ marginTop: 20 }}
      >
        {saving ? 'Saving...' : selected.length === 0 ? 'Continue with All Traditions' : 'Continue'}
      </button>

      <p className="page-subtitle" style={{ marginTop: 16, marginBottom: 0 }}>
        You can personalize your color theme anytime in Settings &mdash; Omnist starts with Saffron
        &amp; Sacred Gold.
      </p>
    </div>
  );
}
